import React, { useEffect, useRef, useState } from "react";
import api from "../api/axios";
import QuestionnaireStepper from "../components/QuestionnaireStepper";
import AnalysisPanel from "../components/AnalysisPanel";
import DoctorReviewForm from "../components/DoctorReviewForm";
import PdfModal from "../components/PdfModal";
import NeuronImpactMap from "../components/NeuronImpactMap";
import SessionAIChatDrawer from "../components/SessionAIChatDrawer";
import "./SessionPage.responsive.css";

const TABS = [
  { key: "questionnaire", label: "Questionnaire", helper: "Patient intake" },
  { key: "analysis", label: "MedGen Analysis", helper: "Clinical reasoning" },
  { key: "review", label: "Doctor Review", helper: "Reviewer notes" },
  { key: "brain", label: "Brain Map", helper: "Neuron impact" },
];

function normalizeAnalysis(rawAnalysis) {
  if (!rawAnalysis) return null;

  const payload = rawAnalysis.analysis_json || {};

  return {
    id: rawAnalysis.id,
    session_id: rawAnalysis.session_id,
    engine_name: rawAnalysis.engine_name,
    engine_version: rawAnalysis.engine_version,
    status: rawAnalysis.status,
    stage: rawAnalysis.stage,
    progress: rawAnalysis.progress,
    error_message: rawAnalysis.error_message,
    raw_text: rawAnalysis.raw_text || payload.raw_text || "",

    initial_summary: payload.initial_summary || "",
    possible_concerns: payload.possible_concerns || [],
    suggested_tests: payload.suggested_tests || [],
    suggested_specialists: payload.suggested_specialists || [],
    medicine_concerns: payload.medicine_concerns || [],
    brain_regions: payload.brain_regions || [],
    doctor_note: payload.doctor_note || "",
    triage: payload.triage || null,
  };
}

function normalizeStreamAnalysis(data) {
  const payload = data?.analysis_json || {};

  return {
    id: data.analysis_id,
    session_id: data.session_id,
    status: data.status,
    stage: data.stage,
    progress: data.progress,
    raw_text: data.raw_text || payload.raw_text || "",

    initial_summary: payload.initial_summary || "",
    possible_concerns: payload.possible_concerns || [],
    suggested_tests: payload.suggested_tests || [],
    suggested_specialists: payload.suggested_specialists || [],
    medicine_concerns: payload.medicine_concerns || [],
    brain_regions: payload.brain_regions || [],
    doctor_note: payload.doctor_note || "",
    triage: payload.triage || null,
  };
}

function parseSseBlock(block) {
  const lines = block.split("\n");
  let event = "message";
  let data = "";

  lines.forEach((line) => {
    if (line.startsWith("event:")) {
      event = line.replace("event:", "").trim();
    }

    if (line.startsWith("data:")) {
      data += line.replace("data:", "").trim();
    }
  });

  try {
    return {
      event,
      data: data ? JSON.parse(data) : {},
    };
  } catch {
    return {
      event,
      data: { text: data },
    };
  }
}

function formatDate(value) {
  if (!value) return "No date";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return "No date";
  }
}

function StatusCard({ label, value, tone = "blue" }) {
  return (
    <div className={`mgsession-status-card ${tone}`}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function SessionSignal({ questionnaireData, analysisData, analysisStreaming }) {
  const completed = questionnaireData ? 1 : 0;
  const analysis = analysisStreaming ? 0.5 : analysisData ? 1 : 0;
  const review = analysisData ? 0.65 : 0.15;

  const signals = [
    {
      label: "Intake",
      value: completed ? "Done" : "Pending",
      height: completed ? "92%" : "28%",
      color: "linear-gradient(180deg, #18b7ff, #1f7bc1)",
    },
    {
      label: "Analysis",
      value: analysisStreaming ? "Live" : analysisData ? "Ready" : "Pending",
      height: `${Math.max(24, analysis * 92)}%`,
      color: "linear-gradient(180deg, #62d7ff, #2d8fe0)",
    },
    {
      label: "Review",
      value: analysisData ? "Open" : "Waiting",
      height: `${Math.max(24, review * 92)}%`,
      color: "linear-gradient(180deg, #fbbf24, #f59e0b)",
    },
  ];

  return (
    <div className="mgsession-signal">
      <div className="mgsession-signal-head">
        <div>
          <span>Session Signal</span>
          <strong>Clinical workflow</strong>
        </div>
        <em>{analysisStreaming ? "Streaming" : "Ready"}</em>
      </div>

      <div className="mgsession-signal-bars">
        {signals.map((item) => (
          <div className="mgsession-signal-item" key={item.label}>
            <div className="mgsession-signal-track">
              <i
                style={{
                  height: item.height,
                  background: item.color,
                }}
              />
            </div>
            <b>{item.value}</b>
            <small>{item.label}</small>
          </div>
        ))}
      </div>
    </div>
  );
}

function SessionWorkflowPipeline({
  questionnaireData,
  analysisData,
  analysisStreaming,
  reviewStatus,
}) {
  const steps = [
    {
      title: "Patient Intake",
      helper: "Questionnaire data",
      active: Boolean(questionnaireData),
      live: !questionnaireData,
    },
    {
      title: "MedGen Analysis",
      helper: analysisStreaming ? "Streaming output" : "Clinical reasoning",
      active: Boolean(analysisData) || analysisStreaming,
      live: analysisStreaming,
    },
    {
      title: "Doctor Review",
      helper: "Reviewer notes",
      active: Boolean(analysisData),
      live: false,
    },
    {
      title: "Brain Map",
      helper: "Neuron impact",
      active: Boolean(analysisData?.brain_regions?.length),
      live: false,
    },
    {
      title: "Case Sheet",
      helper: "PDF output",
      active: Boolean(analysisData),
      live: false,
    },
  ];

  let nextAction = "Complete patient questionnaire";

  if (questionnaireData && analysisStreaming) {
    nextAction = "MedGen analysis is streaming";
  } else if (questionnaireData && !analysisData) {
    nextAction = "Generate MedGen clinical analysis";
  } else if (analysisData) {
    nextAction = "Review analysis and prepare doctor note";
  }

  return (
    <div className="mgsession-workflow-card">
      <div className="mgsession-workflow-head">
        <div>
          <span>Clinical Workflow Intelligence</span>
          <strong>Session pipeline</strong>
        </div>
        <em>{analysisStreaming ? "Live" : "Ready"}</em>
      </div>

      <div className="mgsession-workflow-line">
        {steps.map((step, index) => (
          <div
            key={step.title}
            className={[
              "mgsession-workflow-step",
              step.active ? "active" : "",
              step.live ? "live" : "",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            <div className="mgsession-workflow-node">
              <b>{index + 1}</b>
            </div>

            <div>
              <strong>{step.title}</strong>
              <small>{step.helper}</small>
            </div>
          </div>
        ))}
      </div>

      <div className="mgsession-workflow-bottom">
        <div className="mgsession-mini-metrics">
          <div>
            <span>Intake</span>
            <strong>{questionnaireData ? "Done" : "Pending"}</strong>
          </div>

          <div>
            <span>Analysis</span>
            <strong>
              {analysisStreaming ? "Live" : analysisData ? "Ready" : "Pending"}
            </strong>
          </div>

          <div>
            <span>Reviewer</span>
            <strong>{reviewStatus || "Pending"}</strong>
          </div>
        </div>

        <div className="mgsession-next-action">
          <span>Next Action</span>
          <strong>{nextAction}</strong>
          <p>
            Move this session from intake to analysis, review, brain mapping,
            and clinical sheet output.
          </p>
        </div>
      </div>
    </div>
  );
}

export default function SessionPage({ sessionId, onBack, user }) {
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [questionnaireData, setQuestionnaireData] = useState(null);
  const [analysisData, setAnalysisData] = useState(null);

  const [activeTab, setActiveTab] = useState("questionnaire");
  const [aiChatOpen, setAiChatOpen] = useState(false);

  const [pdfOpen, setPdfOpen] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState("");

  const [analysisStreaming, setAnalysisStreaming] = useState(false);
  const [streamedText, setStreamedText] = useState("");
  const [streamStage, setStreamStage] = useState("");
  const [streamProgress, setStreamProgress] = useState(0);

  const streamAbortRef = useRef(null);

  const loadDetail = async () => {
    try {
      setLoading(true);

      const res = await api.get(`/api/session/detail/${sessionId}`);
      const payload = res.data;

      setDetail(payload);
      setQuestionnaireData(payload.questionnaire?.data || null);
      setAnalysisData(normalizeAnalysis(payload.analysis || null));
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not load session");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (sessionId) {
      loadDetail();
    }

    return () => {
      if (streamAbortRef.current) {
        streamAbortRef.current.abort();
      }
    };
  }, [sessionId]);

  useEffect(() => {
    return () => {
      if (pdfBlobUrl) {
        URL.revokeObjectURL(pdfBlobUrl);
      }
    };
  }, [pdfBlobUrl]);

  const getAuthToken = () => {
    return (
      localStorage.getItem("access_token") ||
      localStorage.getItem("token") ||
      localStorage.getItem("authToken") ||
      ""
    );
  };

  const buildApiUrl = (path) => {
    const baseURL = api.defaults.baseURL || "";
    if (baseURL.startsWith("http")) {
      return `${baseURL}${path}`;
    }
    return path;
  };

  const startAnalysisStream = async (targetSessionId) => {
    try {
      if (streamAbortRef.current) {
        streamAbortRef.current.abort();
      }

      const controller = new AbortController();
      streamAbortRef.current = controller;

      setActiveTab("analysis");
      setAnalysisData(null);
      setAnalysisStreaming(true);
      setStreamedText("");
      setStreamStage("reading_questionnaire");
      setStreamProgress(10);

      const token = getAuthToken();

      const response = await fetch(
        buildApiUrl(`/api/questionnaire/analysis/stream/${targetSessionId}`),
        {
          method: "GET",
          headers: {
            Accept: "text/event-stream",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          signal: controller.signal,
        }
      );

      if (!response.ok) {
        throw new Error(`Analysis stream failed: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");

      let buffer = "";

      while (true) {
        const { value, done } = await reader.read();

        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        const blocks = buffer.split("\n\n");
        buffer = blocks.pop() || "";

        for (const block of blocks) {
          if (!block.trim()) continue;

          const parsed = parseSseBlock(block);

          if (parsed.event === "progress") {
            setStreamStage(parsed.data.stage || "");
            setStreamProgress(parsed.data.progress || 0);
          }

          if (parsed.event === "token") {
            const tokenText = parsed.data.text || "";
            if (tokenText) {
              setStreamedText((prev) => prev + tokenText);
            }
          }

          if (parsed.event === "complete") {
            setStreamStage("completed");
            setStreamProgress(100);
            setAnalysisStreaming(false);
            setAnalysisData(normalizeStreamAnalysis(parsed.data));
            await loadDetail();
          }

          if (parsed.event === "error") {
            setAnalysisStreaming(false);
            setStreamStage("failed");
            setStreamProgress(100);
            alert(parsed.data.message || "Analysis failed.");
          }
        }
      }
    } catch (err) {
      if (err.name === "AbortError") return;

      console.error(err);
      setAnalysisStreaming(false);
      setStreamStage("failed");
      setStreamProgress(100);
      alert(err.message || "Could not start streamed analysis.");
    }
  };

  const openPdf = async () => {
    try {
      setPdfOpen(true);
      setPdfLoading(true);

      if (pdfBlobUrl) {
        URL.revokeObjectURL(pdfBlobUrl);
        setPdfBlobUrl("");
      }

      const response = await api.get(`/api/pdf/case-sheet/${sessionId}`, {
        responseType: "blob",
      });

      const blobUrl = URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" })
      );

      setPdfBlobUrl(blobUrl);
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.detail || "Could not load clinical sheet PDF");
      setPdfOpen(false);
    } finally {
      setPdfLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="mgsession-page">
        <style>{sessionCss}</style>
        <div className="mgsession-loading-card">Loading session...</div>
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="mgsession-page">
        <style>{sessionCss}</style>
        <div className="mgsession-loading-card">Session not found.</div>
      </div>
    );
  }

  const { session, patient } = detail;

  const questionnaireStatus = questionnaireData ? "Completed" : "Pending";
  const analysisStatus = analysisStreaming
    ? "Streaming"
    : analysisData
    ? "Generated"
    : "Pending";

  const reviewStatus = user?.name || "Pending";
  const sessionState = analysisStreaming
    ? "analysis_streaming"
    : session.status || "draft";

  return (
    <>
      <div className="mgsession-page session-page session-workspace">
        <style>{sessionCss}</style>

        <section className="mgsession-hero">
          <div className="mgsession-hero-copy">
            <div className="mgsession-kicker">Session Workspace</div>
            <h1>{session.title || `Clinical Session #${session.id}`}</h1>
            <p>
              Created on {formatDate(session.created_at)}. Review patient intake,
              generate MedGen analysis, prepare doctor review, and inspect brain-map
              impact signals from one workspace.
            </p>

            <div className="mgsession-actions">
              <button
                className="mgsession-btn primary"
                type="button"
                onClick={() => setAiChatOpen(true)}
              >
                💬 Ask MedGen
              </button>

              <button
                className="mgsession-btn soft"
                onClick={openPdf}
                type="button"
              >
                Open Clinical Sheet
              </button>

              <button
                className="mgsession-btn ghost"
                onClick={onBack}
                type="button"
              >
                Back to Dashboard
              </button>
            </div>

            <SessionWorkflowPipeline
              questionnaireData={questionnaireData}
              analysisData={analysisData}
              analysisStreaming={analysisStreaming}
              reviewStatus={reviewStatus}
            />
          </div>

          <div className="mgsession-ai-card">
            <div className="mgsession-ai-top">
              <div>
                <span>Clinical Status</span>
                <strong>
                  {analysisStreaming ? "Analysis streaming" : "Workspace ready"}
                </strong>
              </div>
              <em>{sessionState}</em>
            </div>

            <p>
              Current session is connected with patient profile, questionnaire,
              analysis workflow, PDF case sheet, and MedGen assistant drawer.
            </p>

            <SessionSignal
              questionnaireData={questionnaireData}
              analysisData={analysisData}
              analysisStreaming={analysisStreaming}
            />
          </div>
        </section>

        <section className="mgsession-top-grid">
          <div className="mgsession-card">
            <div className="mgsession-card-head">
              <div>
                <div className="mgsession-kicker blue">Patient Details</div>
                <h2>{patient.name}</h2>
                <p>Patient profile linked to this clinical session.</p>
              </div>
            </div>

            <div className="mgsession-summary-grid">
              <div className="mgsession-summary-item">
                <span>Name</span>
                <strong>{patient.name}</strong>
              </div>

              <div className="mgsession-summary-item">
                <span>Age</span>
                <strong>{patient.age ?? "—"}</strong>
              </div>

              <div className="mgsession-summary-item">
                <span>Gender</span>
                <strong>{patient.gender || "—"}</strong>
              </div>

              <div className="mgsession-summary-item">
                <span>Relation</span>
                <strong>{patient.relation || "self"}</strong>
              </div>
            </div>
          </div>

          <div className="mgsession-dark-card">
            <div className="mgsession-dark-head">
              <div>
                <div className="mgsession-kicker muted-blue">Session Status</div>
                <h2>Workflow state</h2>
              </div>
              <span>Live</span>
            </div>

            <div className="mgsession-status-grid">
              <StatusCard label="Questionnaire" value={questionnaireStatus} />
              <StatusCard label="MedGen Analysis" value={analysisStatus} />
              <StatusCard label="Reviewer" value={reviewStatus} />
              <StatusCard label="Session State" value={sessionState} />
            </div>
          </div>
        </section>

        <nav className="mgsession-tabs">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`mgsession-tab ${activeTab === tab.key ? "active" : ""}`}
              onClick={() => setActiveTab(tab.key)}
            >
              <strong>{tab.label}</strong>
              <span>{tab.helper}</span>
            </button>
          ))}
        </nav>

        <section className="mgsession-tab-panel">
          {activeTab === "questionnaire" && (
            <div className="mgsession-inner-panel questionnaire-scope">
              <QuestionnaireStepper
                sessionId={session.id}
                patient={patient}
                initialData={questionnaireData}
                onSaved={(savedData) => {
                  setQuestionnaireData(savedData);
                  startAnalysisStream(session.id);
                }}
              />
            </div>
          )}

          {activeTab === "analysis" && (
            <div className="mgsession-inner-panel analysis-scope">
              <AnalysisPanel
                analysis={analysisData}
                sessionStatus={
                  analysisStreaming ? "analysis_streaming" : session.status
                }
                streaming={analysisStreaming}
                streamStage={streamStage}
                streamProgress={streamProgress}
                streamedText={streamedText}
              />
            </div>
          )}

          {activeTab === "review" && (
            <div className="mgsession-inner-panel review-scope">
              <DoctorReviewForm
                sessionId={session.id}
                user={user}
                analysis={analysisData}
              />
            </div>
          )}

          {activeTab === "brain" && (
            <div className="mgsession-inner-panel brain-scope">
              <NeuronImpactMap analysis={analysisData} />
            </div>
          )}
        </section>
      </div>

      <SessionAIChatDrawer
        open={aiChatOpen}
        onClose={() => setAiChatOpen(false)}
        sessionId={session.id}
        patient={patient}
      />

      <PdfModal
        open={pdfOpen}
        onClose={() => setPdfOpen(false)}
        pdfBlobUrl={pdfBlobUrl}
        loading={pdfLoading}
      />
    </>
  );
}

const sessionCss = `
.mgsession-page {
  --mg-black: #03060c;
  --mg-navy: #06101f;
  --mg-navy-2: #081525;
  --mg-blue: #1f7bc1;
  --mg-cyan: #18b7ff;
  --mg-text: #07111f;
  --mg-muted: #66778e;
  --mg-border: #cddcec;
  --mg-soft: #f6faff;

  width: 100%;
  min-height: calc(100vh - 68px);
  padding: 28px 34px 46px;
  color: var(--mg-text);
}

.mgsession-page * {
  box-sizing: border-box;
}

.mgsession-loading-card {
  padding: 24px;
  border-radius: 18px;
  background: rgba(255,255,255,0.96);
  border: 1px solid var(--mg-border);
  box-shadow: 0 12px 35px rgba(7, 17, 31, 0.055);
  color: var(--mg-text);
  font-weight: 850;
}

.mgsession-hero {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 430px;
  gap: 26px;
  align-items: stretch;
  margin-bottom: 22px;
  padding: 30px;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid var(--mg-border);
  box-shadow: 0 20px 55px rgba(7, 17, 31, 0.07);
}

.mgsession-kicker {
  margin-bottom: 12px;
  color: #125a94;
  font-size: 11px;
  font-weight: 950;
  letter-spacing: 0.22em;
  text-transform: uppercase;
}

.mgsession-kicker.blue {
  color: #125a94;
}

.mgsession-kicker.muted-blue {
  color: #89bfe8;
}

.mgsession-hero h1 {
  margin: 0;
  color: var(--mg-text);
  font-size: clamp(36px, 4vw, 56px);
  line-height: 1.02;
  letter-spacing: -0.045em;
  font-weight: 950;
}

.mgsession-hero p {
  max-width: 820px;
  margin: 18px 0 18px;
  color: var(--mg-muted);
  font-size: 15px;
  line-height: 1.7;
}

.mgsession-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.mgsession-btn {
  min-height: 40px;
  padding: 10px 16px;
  border-radius: 11px;
  border: 1px solid transparent;
  cursor: pointer;
  font-size: 13px;
  font-weight: 900;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: 0.16s ease;
  white-space: nowrap;
}

.mgsession-btn:disabled {
  opacity: 0.52;
  cursor: not-allowed;
}

.mgsession-btn.primary {
  color: white;
  background: linear-gradient(135deg, #1f7bc1, #0f5f9d);
  box-shadow: 0 14px 28px rgba(31, 123, 193, 0.22);
}

.mgsession-btn.soft,
.mgsession-btn.ghost {
  color: #112238;
  background: #f8fbff;
  border-color: var(--mg-border);
}

/* ==========================================================================
   Animated Session Workflow Pipeline
   ========================================================================== */

.mgsession-workflow-card {
  margin-top: 22px;
  padding: 20px;
  border-radius: 22px;
  background:
    radial-gradient(circle at 96% 0%, rgba(24, 183, 255, 0.13), transparent 34%),
    linear-gradient(180deg, #ffffff 0%, #f7fbff 100%);
  border: 1px solid var(--mg-border);
  box-shadow: 0 14px 38px rgba(7, 17, 31, 0.055);
}

.mgsession-workflow-head {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  align-items: flex-start;
  margin-bottom: 18px;
}

.mgsession-workflow-head span {
  display: block;
  margin-bottom: 7px;
  color: #125a94;
  font-size: 10px;
  font-weight: 950;
  letter-spacing: 0.17em;
  text-transform: uppercase;
}

.mgsession-workflow-head strong {
  display: block;
  color: var(--mg-text);
  font-size: 22px;
  font-weight: 950;
  letter-spacing: -0.045em;
}

.mgsession-workflow-head em {
  padding: 6px 11px;
  border-radius: 999px;
  color: #0b4e84;
  background: rgba(31, 123, 193, 0.10);
  border: 1px solid rgba(31, 123, 193, 0.18);
  font-size: 11px;
  font-weight: 900;
  font-style: normal;
  white-space: nowrap;
}

.mgsession-workflow-line {
  position: relative;
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 20px;
}

.mgsession-workflow-line::before {
  content: "";
  position: absolute;
  left: 8%;
  right: 8%;
  top: 23px;
  height: 3px;
  border-radius: 999px;
  background:
    linear-gradient(
      90deg,
      rgba(31,123,193,0.12),
      rgba(24,183,255,0.42),
      rgba(31,123,193,0.12)
    );
  background-size: 220% 100%;
  z-index: 0;
  animation: mgsessionPipelineFlow 2.8s linear infinite;
  box-shadow: 0 0 18px rgba(24, 183, 255, 0.18);
}

.mgsession-workflow-line::after {
  content: "";
  position: absolute;
  top: 19px;
  left: 8%;
  width: 11px;
  height: 11px;
  border-radius: 999px;
  z-index: 2;
  background: #18b7ff;
  box-shadow:
    0 0 0 6px rgba(24, 183, 255, 0.12),
    0 0 22px rgba(24, 183, 255, 0.55);
  animation: mgsessionPipelineDot 4s ease-in-out infinite;
}

.mgsession-workflow-step {
  position: relative;
  z-index: 1;
  display: grid;
  justify-items: center;
  text-align: center;
  gap: 8px;
  min-width: 0;
  animation: mgsessionStepFloat 0.7s ease both;
}

.mgsession-workflow-step:nth-child(1) {
  animation-delay: 0.04s;
}

.mgsession-workflow-step:nth-child(2) {
  animation-delay: 0.12s;
}

.mgsession-workflow-step:nth-child(3) {
  animation-delay: 0.20s;
}

.mgsession-workflow-step:nth-child(4) {
  animation-delay: 0.28s;
}

.mgsession-workflow-step:nth-child(5) {
  animation-delay: 0.36s;
}

.mgsession-workflow-node {
  position: relative;
  width: 46px;
  height: 46px;
  border-radius: 16px;
  display: grid;
  place-items: center;
  background: #eef6ff;
  border: 1px solid var(--mg-border);
  box-shadow: 0 8px 18px rgba(7, 17, 31, 0.05);
}

.mgsession-workflow-node b {
  color: #0b4e84;
  font-size: 13px;
  font-weight: 950;
}

.mgsession-workflow-step.active .mgsession-workflow-node {
  background:
    radial-gradient(circle at 30% 20%, rgba(255,255,255,0.28), transparent 28%),
    linear-gradient(135deg, #1f7bc1, #0f5f9d);
  border-color: transparent;
  box-shadow:
    0 14px 28px rgba(31, 123, 193, 0.22),
    0 0 0 0 rgba(24, 183, 255, 0.22);
  animation: mgsessionNodeBreath 2.2s ease-in-out infinite;
}

.mgsession-workflow-step.active .mgsession-workflow-node b {
  color: white;
}

.mgsession-workflow-step.live .mgsession-workflow-node::after,
.mgsession-workflow-step.active .mgsession-workflow-node::after {
  content: "";
  position: absolute;
  width: 9px;
  height: 9px;
  right: -2px;
  top: -2px;
  border-radius: 50%;
  background: #18b7ff;
  box-shadow:
    0 0 0 6px rgba(24, 183, 255, 0.14),
    0 0 20px rgba(24, 183, 255, 0.52);
  animation: mgsessionPulse 1.8s infinite;
}

.mgsession-workflow-step strong {
  color: var(--mg-text);
  font-size: 12px;
  font-weight: 950;
  line-height: 1.2;
}

.mgsession-workflow-step small {
  display: block;
  margin-top: 3px;
  color: var(--mg-muted);
  font-size: 10px;
  font-weight: 750;
  line-height: 1.25;
}

.mgsession-workflow-bottom {
  display: grid;
  grid-template-columns: 280px minmax(0, 1fr);
  gap: 16px;
  align-items: stretch;
}

.mgsession-mini-metrics {
  display: grid;
  gap: 10px;
  padding: 16px;
  border-radius: 18px;
  background: #eef6ff;
  border: 1px solid #d9e8f6;
}

.mgsession-mini-metrics div {
  min-height: 54px;
  padding: 12px;
  border-radius: 14px;
  background: #ffffff;
  border: 1px solid #d9e8f6;
}

.mgsession-mini-metrics span,
.mgsession-next-action span {
  display: block;
  margin-bottom: 6px;
  color: #125a94;
  font-size: 10px;
  font-weight: 950;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.mgsession-mini-metrics strong {
  color: var(--mg-text);
  font-size: 14px;
  font-weight: 950;
  overflow-wrap: anywhere;
}

.mgsession-next-action {
  padding: 18px;
  border-radius: 18px;
  background:
    radial-gradient(circle at 100% 0%, rgba(31, 123, 193, 0.10), transparent 34%),
    #ffffff;
  border: 1px solid #d9e8f6;
}

.mgsession-next-action strong {
  display: block;
  color: var(--mg-text);
  font-size: 18px;
  font-weight: 950;
  letter-spacing: -0.035em;
}

.mgsession-next-action p {
  margin: 10px 0 0;
  color: var(--mg-muted);
  font-size: 13px;
  line-height: 1.55;
}

.mgsession-ai-card {
  padding: 22px;
  border-radius: 22px;
  color: white;
  background:
    radial-gradient(circle at 92% 0%, rgba(24, 183, 255, 0.20), transparent 34%),
    linear-gradient(145deg, #03060c, #081525 62%, #06101f);
  border: 1px solid rgba(24,183,255,0.18);
  box-shadow: 0 20px 55px rgba(3, 6, 12, 0.16);
}

.mgsession-ai-top {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  align-items: flex-start;
}

.mgsession-ai-top span {
  display: block;
  margin-bottom: 8px;
  color: #9fdcff;
  font-size: 10px;
  font-weight: 950;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}

.mgsession-ai-top strong {
  color: white;
  font-size: 22px;
  letter-spacing: -0.04em;
}

.mgsession-ai-top em {
  max-width: 150px;
  padding: 5px 10px;
  border-radius: 999px;
  font-style: normal;
  color: #bfeaff;
  background: rgba(24,183,255,0.12);
  border: 1px solid rgba(24,183,255,0.18);
  font-size: 11px;
  font-weight: 900;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mgsession-ai-card p {
  color: #a9bdd3;
  margin: 18px 0;
  line-height: 1.65;
}

.mgsession-signal {
  padding: 16px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.075);
  border: 1px solid rgba(24,183,255,0.13);
}

.mgsession-signal-head {
  display: flex;
  justify-content: space-between;
  gap: 12px;
}

.mgsession-signal-head span {
  display: block;
  color: #9fdcff;
  font-size: 10px;
  font-weight: 950;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.mgsession-signal-head strong {
  color: white;
  font-size: 16px;
}

.mgsession-signal-head em {
  color: #bfeaff;
  font-style: normal;
  font-size: 11px;
  font-weight: 900;
}

.mgsession-signal-bars {
  height: 145px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
  align-items: end;
  margin-top: 16px;
}

.mgsession-signal-item {
  display: grid;
  justify-items: center;
  gap: 7px;
}

.mgsession-signal-track {
  width: 58px;
  height: 88px;
  display: flex;
  align-items: end;
  padding: 5px;
  border-radius: 16px;
  background: rgba(255,255,255,0.10);
  border: 1px solid rgba(255,255,255,0.10);
}

.mgsession-signal-track i {
  width: 100%;
  min-height: 18px;
  border-radius: 12px;
  animation: mgsessionBarRise 0.7s ease both;
}

.mgsession-signal-item b {
  color: white;
  font-size: 15px;
  font-weight: 950;
}

.mgsession-signal-item small {
  color: #a9bdd3;
  font-size: 11px;
  font-weight: 850;
}

.mgsession-top-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.25fr) minmax(360px, 0.85fr);
  gap: 18px;
  margin-bottom: 18px;
}

.mgsession-card {
  padding: 22px;
  border-radius: 22px;
  background: rgba(255,255,255,0.96);
  border: 1px solid var(--mg-border);
  box-shadow: 0 12px 35px rgba(7, 17, 31, 0.055);
}

.mgsession-card-head {
  margin-bottom: 18px;
}

.mgsession-card h2,
.mgsession-dark-card h2 {
  margin: 0;
  color: var(--mg-text);
  font-size: 25px;
  letter-spacing: -0.045em;
  font-weight: 950;
}

.mgsession-card p {
  margin: 8px 0 0;
  color: var(--mg-muted);
  line-height: 1.55;
}

.mgsession-summary-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.mgsession-summary-item {
  min-height: 92px;
  padding: 18px;
  border-radius: 16px;
  background: #f7fbff;
  border: 1px solid var(--mg-border);
}

.mgsession-summary-item span {
  display: block;
  margin-bottom: 10px;
  color: #75869d;
  font-size: 10px;
  font-weight: 950;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.mgsession-summary-item strong {
  color: var(--mg-text);
  font-size: 17px;
  font-weight: 950;
}

.mgsession-dark-card {
  padding: 22px;
  border-radius: 22px;
  color: white;
  background:
    radial-gradient(circle at 92% 0%, rgba(24, 183, 255, 0.20), transparent 34%),
    linear-gradient(145deg, #03060c, #081525 62%, #06101f);
  border: 1px solid rgba(24,183,255,0.18);
  box-shadow: 0 20px 55px rgba(3, 6, 12, 0.16);
}

.mgsession-dark-head {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  align-items: flex-start;
  margin-bottom: 16px;
}

.mgsession-dark-head h2 {
  color: white;
}

.mgsession-dark-head > span {
  padding: 5px 10px;
  border-radius: 999px;
  color: #bfeaff;
  background: rgba(24,183,255,0.12);
  border: 1px solid rgba(24,183,255,0.18);
  font-size: 11px;
  font-weight: 900;
}

.mgsession-status-grid {
  display: grid;
  gap: 12px;
}

.mgsession-status-card {
  min-height: 72px;
  padding: 16px;
  border-radius: 15px;
  background: rgba(255,255,255,0.96);
  border: 1px solid rgba(194, 218, 244, 0.55);
}

.mgsession-status-card span {
  display: block;
  margin-bottom: 7px;
  color: #75869d;
  font-size: 10px;
  font-weight: 950;
  letter-spacing: 0.13em;
  text-transform: uppercase;
}

.mgsession-status-card strong {
  display: block;
  color: var(--mg-text);
  font-size: 15px;
  font-weight: 950;
  overflow-wrap: anywhere;
}

.mgsession-tabs {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin: 18px 0;
}

.mgsession-tab {
  min-height: 54px;
  padding: 10px 16px;
  border-radius: 14px;
  border: 1px solid var(--mg-border);
  background: rgba(255,255,255,0.95);
  cursor: pointer;
  display: grid;
  gap: 3px;
  text-align: left;
  min-width: 160px;
  transition: 0.16s ease;
}

.mgsession-tab strong {
  color: var(--mg-text);
  font-size: 13px;
  font-weight: 950;
}

.mgsession-tab span {
  color: var(--mg-muted);
  font-size: 11px;
  font-weight: 750;
}

.mgsession-tab.active {
  background: linear-gradient(135deg, #1f7bc1, #0f5f9d);
  border-color: transparent;
  box-shadow: 0 14px 28px rgba(31, 123, 193, 0.22);
}

.mgsession-tab.active strong,
.mgsession-tab.active span {
  color: white;
}

.mgsession-tab-panel {
  min-width: 0;
}

.mgsession-inner-panel {
  min-width: 0;
  border-radius: 24px;
}

/* Child component safety */
.mgsession-page .card,
.mgsession-page .panel,
.mgsession-page .summary-item,
.mgsession-page .status-item,
.mgsession-page .questionnaire-card,
.mgsession-page .analysis-card,
.mgsession-page .review-card {
  color: var(--mg-text) !important;
}

.mgsession-page .card:not(.dark-card),
.mgsession-page .panel:not(.dark-card),
.mgsession-page .summary-item,
.mgsession-page .status-item {
  background: rgba(255,255,255,0.96);
  border-color: var(--mg-border);
}

.mgsession-page .dark-card,
.mgsession-page .dark-panel,
.mgsession-page .brain-canvas-card,
.mgsession-page .brain-side-card,
.mgsession-page .brain-selected-panel {
  border-radius: 24px;
}

.mgsession-page .dark-card > h1,
.mgsession-page .dark-card > h2,
.mgsession-page .dark-card > h3,
.mgsession-page .dark-panel > h1,
.mgsession-page .dark-panel > h2,
.mgsession-page .dark-panel > h3 {
  color: #ffffff !important;
}

.mgsession-page .status-item,
.mgsession-page .dark-status-item {
  background: rgba(255,255,255,0.96) !important;
  color: var(--mg-text) !important;
  border: 1px solid var(--mg-border) !important;
}

.mgsession-page .status-item span,
.mgsession-page .dark-status-item span {
  color: #75869d !important;
}

.mgsession-page .status-item strong,
.mgsession-page .dark-status-item strong {
  color: var(--mg-text) !important;
}

.mgsession-page .questionnaire-header,
.mgsession-page .questionnaire-step-strip,
.mgsession-page .questionnaire-form-body,
.mgsession-page .questionnaire-sticky-footer {
  border-color: var(--mg-border);
}

.mgsession-page .questionnaire-sticky-footer {
  background: rgba(255,255,255,0.90) !important;
  backdrop-filter: blur(16px);
}

.mgsession-page .analysis-stream-box,
.mgsession-page textarea,
.mgsession-page input,
.mgsession-page select {
  border-color: var(--mg-border);
}

.mgsession-page textarea,
.mgsession-page input,
.mgsession-page select {
  color: var(--mg-text);
}

.mgsession-page .brain-scope {
  background:
    radial-gradient(circle at 22% 0%, rgba(24, 183, 255, 0.10), transparent 34%),
    transparent;
}

@keyframes mgsessionBarRise {
  from {
    transform: scaleY(0.4);
    opacity: 0.45;
    transform-origin: bottom;
  }

  to {
    transform: scaleY(1);
    opacity: 1;
    transform-origin: bottom;
  }
}

@keyframes mgsessionPulse {
  0% {
    box-shadow: 0 0 0 0 rgba(24, 183, 255, 0.22);
  }

  70% {
    box-shadow: 0 0 0 10px rgba(24, 183, 255, 0);
  }

  100% {
    box-shadow: 0 0 0 0 rgba(24, 183, 255, 0);
  }
}

@keyframes mgsessionPipelineFlow {
  0% {
    background-position: 220% 0;
  }

  100% {
    background-position: -220% 0;
  }
}

@keyframes mgsessionPipelineDot {
  0% {
    left: 8%;
    opacity: 0;
    transform: scale(0.82);
  }

  8% {
    opacity: 1;
    transform: scale(1);
  }

  25% {
    left: 29%;
  }

  50% {
    left: 50%;
  }

  75% {
    left: 71%;
  }

  92% {
    opacity: 1;
  }

  100% {
    left: 88%;
    opacity: 0;
    transform: scale(0.82);
  }
}

@keyframes mgsessionStepFloat {
  from {
    opacity: 0;
    transform: translateY(10px) scale(0.96);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes mgsessionNodeBreath {
  0% {
    box-shadow:
      0 14px 28px rgba(31, 123, 193, 0.22),
      0 0 0 0 rgba(24, 183, 255, 0.18);
  }

  50% {
    box-shadow:
      0 18px 36px rgba(31, 123, 193, 0.28),
      0 0 0 7px rgba(24, 183, 255, 0.06);
  }

  100% {
    box-shadow:
      0 14px 28px rgba(31, 123, 193, 0.22),
      0 0 0 0 rgba(24, 183, 255, 0.18);
  }
}

@media (max-width: 1180px) {
  .mgsession-page {
    padding: 20px;
  }

  .mgsession-hero {
    grid-template-columns: 1fr;
  }

  .mgsession-ai-card {
    max-width: 620px;
  }

  .mgsession-top-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 900px) {
  .mgsession-workflow-line {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .mgsession-workflow-line::before,
  .mgsession-workflow-line::after {
    display: none;
  }

  .mgsession-workflow-step {
    grid-template-columns: 46px 1fr;
    justify-items: start;
    text-align: left;
    padding: 12px;
    border-radius: 16px;
    background: rgba(255,255,255,0.75);
    border: 1px solid #d9e8f6;
  }

  .mgsession-workflow-bottom {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 760px) {
  .mgsession-page {
    padding: 14px;
  }

  .mgsession-hero {
    padding: 20px;
  }

  .mgsession-hero h1 {
    font-size: 34px;
  }

  .mgsession-actions {
    flex-direction: column;
  }

  .mgsession-btn {
    width: 100%;
  }

  .mgsession-summary-grid {
    grid-template-columns: 1fr;
  }

  .mgsession-tabs {
    display: grid;
    grid-template-columns: 1fr;
  }

  .mgsession-tab {
    width: 100%;
  }

  .mgsession-signal-bars {
    gap: 8px;
  }

  .mgsession-signal-track {
    width: 48px;
  }
}
`;